import { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { Botao, IconeTema, Janela, Selo } from "@/Logigate/ui";

/**
 * Aviso único (1x por browser) sobre os serviços pagos na Escola Inteligente:
 * o transporte por mês, a folha de prova por período, a aba «Serviços pagos» e o
 * filtro «Artigo pago».
 *
 * - Aparece logo ao entrar na Escola Inteligente; ao fechar, fica marcado no localStorage.
 * - Janela temporária: deixa de aparecer a partir de DATA_LIMITE, mesmo para
 *   quem nunca o viu.
 *
 * Montado no casco do painel da Escola e no do Logigate (secção «Escola Inteligente»).
 * Segue o modelo do AvisoChangeFactura.
 */

// A partir desta data o aviso já não aparece a ninguém (janela de 3 semanas).
const DATA_LIMITE = new Date("2026-10-28T23:59:59");
const STORAGE_KEY = "aviso_servicos_pagos_visto_v1";

// O artigo «Serviços Pagos» da Central de Ajuda (ajuda/escola-inteligente/financeiro/09_servicos_pagos.md).
const ARTIGO_AJUDA_ID = 647;

const NOVIDADES = [
    { icone: "mdi:calendar-month-outline", texto: <>Ao facturar, escolha os <strong>meses</strong> ou as <strong>datas</strong> que o aluno está a pagar.</> },
    { icone: "mdi:card-account-details-outline", texto: <>Vê tudo na nova aba <strong>Serviços pagos</strong> da ficha do aluno.</> },
    { icone: "mdi:filter-variant", texto: <>Filtra a lista de alunos por <strong>Artigo pago</strong>.</> },
];

export default function AvisoServicosPagos() {
    const [aberto, setAberto] = useState(false);

    useEffect(() => {
        try {
            const dentroDaJanela = new Date() <= DATA_LIMITE;
            const jaViu = localStorage.getItem(STORAGE_KEY);
            if (dentroDaJanela && !jaViu) {
                setAberto(true);
            }
        } catch (e) {
            // localStorage indisponível (modo privado, etc.) — não mostrar.
        }
    }, []);

    const fechar = () => {
        try {
            localStorage.setItem(STORAGE_KEY, new Date().toISOString());
        } catch (e) {
            // ignorar — apenas não persiste, mas fecha à mesma.
        }
        setAberto(false);
    };

    const ajuda = (window as any).urls?.ajuda;

    return (
        <Janela
            aberta={aberto}
            onFechar={fechar}
            confirmarAoFechar={false}
            largura="lg"
            titulo={
                <span className="flex items-center gap-3">
                    <IconeTema cor="marca" redondo>
                        <Icon icon="solar:bag-check-bold" />
                    </IconeTema>
                    <span className="min-w-0">
                        <Selo cor="marca" tamanho="xs" className="mb-0.5">Novidade</Selo>
                        <span className="block text-base font-semibold leading-6">Serviços pagos</span>
                    </span>
                </span>
            }
            rodape={
                <>
                    {ajuda && ARTIGO_AJUDA_ID && (
                        <Botao
                            variante="neutro"
                            href={`${ajuda}/a/${ARTIGO_AJUDA_ID}`}
                            externo
                            icone={<Icon icon="solar:book-2-bold" className="size-[18px]" />}
                            className="h-11 flex-1 sm:h-9 sm:flex-none"
                        >
                            Ler na ajuda
                        </Botao>
                    )}
                    <Botao onClick={fechar} icone={<Icon icon="mdi:check" className="size-[18px]" />} className="h-11 flex-1 sm:h-9 sm:flex-none">
                        Percebi
                    </Botao>
                </>
            }
        >
            <div className="space-y-4">
                <p className="text-sm leading-relaxed text-zinc-600 dark:text-zinc-300">
                    Agora a escola sabe o que cada aluno pagou do <strong className="font-semibold text-zinc-900 dark:text-zinc-100">transporte</strong>, da{" "}
                    <strong className="font-semibold text-zinc-900 dark:text-zinc-100">folha de prova</strong> e de outros serviços.
                </p>

                <ul className="space-y-2.5">
                    {NOVIDADES.map((n) => (
                        <li key={n.icone} className="flex items-start gap-3 text-sm leading-relaxed text-zinc-600 dark:text-zinc-300 [&_strong]:font-semibold [&_strong]:text-zinc-900 dark:[&_strong]:text-zinc-100">
                            <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg bg-portal-suave text-portal-texto dark:bg-portal-claro/10 dark:text-portal-claro">
                                <Icon icon={n.icone} className="size-4" />
                            </span>
                            <span className="min-w-0 pt-0.5">{n.texto}</span>
                        </li>
                    ))}
                </ul>

                <div>
                    <p className="mb-2 text-[13px] font-medium text-zinc-500 dark:text-zinc-400">Vê no vídeo como se faz:</p>
                    <video
                        src="/servico-pago.mp4"
                        controls
                        playsInline
                        preload="metadata"
                        className="aspect-video max-h-[420px] w-full rounded-xl bg-black ring-1 ring-zinc-950/10 dark:ring-white/10"
                    >
                        O teu navegador não suporta a reprodução de vídeo.
                    </video>
                </div>
            </div>
        </Janela>
    );
}
